import React, { useEffect, useMemo, useState } from 'react';
import { getTodaySalaahTimes, type SalaahTimesTodayResponse, type SalaahTimings } from '../';

const JUMUAH_KHUTBAH_TIME = '13:00';
const JUMUAH_SALAAH_TIME = '13:30';
const DAY_MINUTES = 24 * 60;
const CLOCK_TICK_MS = 30_000;

type RowKey = 'fajr' | 'sunrise' | 'dhuhr' | 'asr' | 'maghrib' | 'isha';

const ROWS: Array<{ key: RowKey; label: string; icon: string; isPrayer: boolean }> = [
  { key: 'fajr', label: 'Fajr', icon: 'bi-sunrise', isPrayer: true },
  { key: 'sunrise', label: 'Sunrise', icon: 'bi-sun', isPrayer: false },
  { key: 'dhuhr', label: 'Dhuhr', icon: 'bi-brightness-high', isPrayer: true },
  { key: 'asr', label: 'Asr', icon: 'bi-brightness-alt-high', isPrayer: true },
  { key: 'maghrib', label: 'Maghrib', icon: 'bi-sunset', isPrayer: true },
  { key: 'isha', label: 'Isha', icon: 'bi-moon-stars', isPrayer: true },
];

const PRAYERS = ROWS.filter((row) => row.isPrayer);

function toMinutes(time: string): number | null {
  const [h, m] = time.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

function formatCountdown(minutes: number): string {
  if (minutes <= 0) return 'now';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `in ${m} min`;
  return `in ${h}h ${String(m).padStart(2, '0')}m`;
}

interface NextPrayer {
  key: RowKey;
  label: string;
  time: string;
  isTomorrow: boolean;
  minutesUntil: number;
  progress: number;
}

function getNextPrayer(timings: SalaahTimings, nowMinutes: number): NextPrayer | null {
  const scheduled = PRAYERS.map((p) => ({ ...p, minutes: toMinutes(timings[p.key]) })).filter(
    (p): p is (typeof PRAYERS)[number] & { minutes: number } => p.minutes !== null
  );
  if (scheduled.length === 0) return null;

  const nextIdx = scheduled.findIndex((p) => p.minutes > nowMinutes);
  const isTomorrow = nextIdx === -1;
  const next = isTomorrow ? scheduled[0] : scheduled[nextIdx];
  const nextAt = isTomorrow ? next.minutes + DAY_MINUTES : next.minutes;
  const prev = nextIdx > 0 ? scheduled[nextIdx - 1] : scheduled[scheduled.length - 1];
  const prevAt = nextIdx > 0 ? prev.minutes : prev.minutes - (isTomorrow ? 0 : DAY_MINUTES);
  const span = Math.max(1, nextAt - prevAt);

  return {
    key: next.key,
    label: next.label,
    time: timings[next.key],
    isTomorrow,
    minutesUntil: nextAt - nowMinutes,
    progress: Math.min(100, Math.max(0, ((nowMinutes - prevAt) / span) * 100)),
  };
}

function useMinuteClock(): number {
  const read = () => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  };
  const [minutes, setMinutes] = useState(read);
  useEffect(() => {
    const id = setInterval(() => setMinutes(read()), CLOCK_TICK_MS);
    return () => clearInterval(id);
  }, []);
  return minutes;
}

const SectionShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <section id="salaah" className="salaah-v2" aria-labelledby="salaah-title">
    <div className="container salaah-v2__container">{children}</div>
  </section>
);

const SectionIntro: React.FC<{ date?: string; hijriDate?: string }> = ({ date, hijriDate }) => (
  <header className="salaah-v2__intro">
    <span className="salaah-v2__eyebrow">Daily Prayers</span>
    <h2 id="salaah-title" className="salaah-v2__title">
      Salaah Times
    </h2>
    {date ? (
      <p className="salaah-v2__dates">
        <span>{date}</span>
        {hijriDate ? (
          <>
            <span className="salaah-v2__dates-sep" aria-hidden>
              ·
            </span>
            <span className="salaah-v2__hijri">{hijriDate}</span>
          </>
        ) : null}
      </p>
    ) : null}
  </header>
);

const LoadingSkeleton: React.FC = () => (
  <SectionShell>
    <div className="salaah-v2__layout" aria-busy="true" aria-label="Loading salaah times">
      <div>
        <SectionIntro />
        <div className="salaah-v2__skeleton salaah-v2__skeleton--card" />
      </div>
      <div className="salaah-v2__list">
        {ROWS.map((row) => (
          <div key={row.key} className="salaah-v2__skeleton salaah-v2__skeleton--row" />
        ))}
      </div>
    </div>
  </SectionShell>
);

const ErrorState: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <SectionShell>
    <SectionIntro />
    <div className="salaah-v2__error" role="alert">
      <i className="bi bi-exclamation-triangle-fill" aria-hidden />
      <span>Unable to load salaah times at the moment.</span>
      <button type="button" className="salaah-v2__retry" onClick={onRetry}>
        Try again
      </button>
    </div>
  </SectionShell>
);

export const SalaahTimesSection: React.FC = () => {
  const [data, setData] = useState<SalaahTimesTodayResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const nowMinutes = useMinuteClock();

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await getTodaySalaahTimes();
      setData(result);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const next = useMemo(
    () => (data ? getNextPrayer(data.timings, nowMinutes) : null),
    [data, nowMinutes]
  );

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error || !data) {
    return <ErrorState onRetry={fetchData} />;
  }

  const { timings, date, hijriDate } = data;
  const isFriday = new Date().getDay() === 5;

  return (
    <SectionShell>
      <div className="salaah-v2__layout">
        <div className="salaah-v2__aside">
          <SectionIntro date={date} hijriDate={hijriDate} />

          {next ? (
            <div className="salaah-v2__next" aria-live="polite">
              <span className="salaah-v2__next-label">
                Next salaah{next.isTomorrow ? ' · tomorrow' : ''}
              </span>
              <div className="salaah-v2__next-main">
                <span className="salaah-v2__next-name">{next.label}</span>
                <span className="salaah-v2__next-time">{next.time}</span>
              </div>
              <span className="salaah-v2__next-countdown">{formatCountdown(next.minutesUntil)}</span>
              <div className="salaah-v2__next-progress" aria-hidden>
                <span style={{ width: `${next.progress}%` }} />
              </div>
            </div>
          ) : null}

          <div className={`salaah-v2__jumuah${isFriday ? ' is-today' : ''}`}>
            <div className="salaah-v2__jumuah-head">
              <i className="bi bi-moon-stars-fill" aria-hidden />
              <span>Jumu&apos;ah</span>
              <span className="salaah-v2__jumuah-tag">{isFriday ? 'Today' : 'Every Friday'}</span>
            </div>
            <dl className="salaah-v2__jumuah-times">
              <div>
                <dt>Khutbah</dt>
                <dd>{JUMUAH_KHUTBAH_TIME}</dd>
              </div>
              <div>
                <dt>Salaah</dt>
                <dd>{JUMUAH_SALAAH_TIME}</dd>
              </div>
            </dl>
          </div>
        </div>

        <ol className="salaah-v2__list">
          {ROWS.map((row) => {
            const minutes = toMinutes(timings[row.key]);
            const isNext = next?.key === row.key;
            const isPast =
              !isNext && (Boolean(next?.isTomorrow) || (minutes !== null && minutes <= nowMinutes));
            const classes = [
              'salaah-v2__row',
              !row.isPrayer ? 'is-sunrise' : '',
              isNext ? 'is-next' : '',
              isPast ? 'is-past' : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <li key={row.key} className={classes} aria-current={isNext ? 'time' : undefined}>
                <span className="salaah-v2__row-icon" aria-hidden>
                  <i className={`bi ${row.icon}`} />
                </span>
                <span className="salaah-v2__row-name">
                  {row.label}
                  {!row.isPrayer ? <span className="salaah-v2__row-note">Fajr ends</span> : null}
                  {isFriday && row.key === 'dhuhr' ? (
                    <span className="salaah-v2__row-note">Jumu&apos;ah {JUMUAH_SALAAH_TIME}</span>
                  ) : null}
                </span>
                {isNext ? <span className="salaah-v2__row-badge">Next</span> : null}
                <span className="salaah-v2__row-time">{timings[row.key]}</span>
              </li>
            );
          })}
        </ol>
      </div>
    </SectionShell>
  );
};
